import {
  hexToHsva,
  hsvaToHex,
  hsvaToHexa,
  hsvaToHslString,
} from '@uiw/color-convert';
import Alpha from '@uiw/react-color-alpha';
import Hue from '@uiw/react-color-hue';
import Saturation from '@uiw/react-color-saturation';
import React from 'react';

import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// ── Recent colors helpers ──────────────────────────────────────────────────
const RECENT_KEY = (pid) => `orbis_recent_colors_${pid || 'global'}`;

function getRecent(pid) {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY(pid)) || '[]');
  } catch {
    return [];
  }
}

function pushRecent(pid, color) {
  try {
    const list = getRecent(pid);
    const next = [color, ...list.filter((c) => c !== color)].slice(0, 12);
    localStorage.setItem(RECENT_KEY(pid), JSON.stringify(next));
  } catch {
    // ignore
  }
}

// ── Gradient parsing helpers ───────────────────────────────────────────────
function parseLinear(val) {
  // e.g. "linear-gradient(135deg, #abc 0%, #def 100%)"
  const m = val.match(
    /linear-gradient\(\s*([\d.]+)deg\s*,\s*(#[0-9a-fA-F]{3,8})\s+([\d.]+)%\s*,\s*(#[0-9a-fA-F]{3,8})\s+([\d.]+)%/
  );
  if (!m) return null;
  return {
    angle: parseFloat(m[1]),
    stops: [
      { color: m[2], position: parseFloat(m[3]) },
      { color: m[4], position: parseFloat(m[5]) },
    ],
  };
}

function parseRadial(val) {
  // e.g. "radial-gradient(circle at 50% 50%, #abc 0%, #def 100%)"
  const m = val.match(
    /radial-gradient\(\s*circle at\s+([\d.]+)%\s+([\d.]+)%\s*,\s*(#[0-9a-fA-F]{3,8})\s+([\d.]+)%\s*,\s*(#[0-9a-fA-F]{3,8})\s+([\d.]+)%/
  );
  if (!m) return null;
  return {
    posX: parseFloat(m[1]),
    posY: parseFloat(m[2]),
    stops: [
      { color: m[3], position: parseFloat(m[4]) },
      { color: m[4], position: parseFloat(m[5]) },
    ],
  };
}

function buildLinear(angle, stops) {
  return `linear-gradient(${angle}deg, ${stops[0].color} ${stops[0].position}%, ${stops[1].color} ${stops[1].position}%)`;
}

function buildRadial(posX, posY, stops) {
  return `radial-gradient(circle at ${posX}% ${posY}%, ${stops[0].color} 0%, ${stops[1].color} 100%)`;
}

const SLIDER_CLASS = '[&>div:first-child]:overflow-hidden [&>div:first-child]:!rounded-md';
const SLIDER_STYLE = { width: '100%', height: '12px', borderRadius: '6px' };

// ── Gradient stop selector ─────────────────────────────────────────────────
function StopSelector({ stops, activeStop, setActiveStop }) {
  return (
    <div className="flex gap-2">
      {stops.map((s, i) => (
        <button
          key={i}
          type="button"
          onClick={() => setActiveStop(i)}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors"
          style={{
            background: activeStop === i ? 'var(--orbis-s2)' : 'transparent',
            border: `1px solid ${activeStop === i ? 'var(--orbis-s3)' : 'var(--orbis-s2)'}`,
            color: 'var(--orbis-t2)',
          }}
        >
          <span
            className="w-3 h-3 rounded-sm shrink-0 border border-[rgba(255,255,255,0.15)]"
            style={{ background: s.color }}
          />
          {i === 0 ? 'From' : 'To'}
        </button>
      ))}
    </div>
  );
}

// ── Radial position grid (3×3) ─────────────────────────────────────────────
const RADIAL_POSITIONS = [
  { label: 'TL', x: 0, y: 0 },
  { label: 'TC', x: 50, y: 0 },
  { label: 'TR', x: 100, y: 0 },
  { label: 'ML', x: 0, y: 50 },
  { label: 'C', x: 50, y: 50 },
  { label: 'MR', x: 100, y: 50 },
  { label: 'BL', x: 0, y: 100 },
  { label: 'BC', x: 50, y: 100 },
  { label: 'BR', x: 100, y: 100 },
];

function RadialGrid({ posX, posY, onChange }) {
  return (
    <div>
      <span className="text-[10px] text-[var(--orbis-t4)] mb-1 block">Origin</span>
      <div className="grid grid-cols-3 gap-0.5" style={{ width: 72 }}>
        {RADIAL_POSITIONS.map((p) => {
          const isActive = p.x === posX && p.y === posY;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => onChange(p.x, p.y)}
              className="w-5 h-5 rounded-sm transition-colors"
              style={{
                background: isActive ? 'var(--orbis-blue)' : 'var(--orbis-s2)',
                border: `1px solid ${isActive ? 'rgba(184,204,223,0.5)' : 'var(--orbis-s3)'}`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Main ColorPicker ───────────────────────────────────────────────────────
/**
 * Props:
 *   value         – current color string (hex or CSS gradient)
 *   onValueChange – called with new value on every change
 *   projectId     – optional; scopes recent colors in localStorage
 *   mode          – "solid" | "any" (default "any"). "solid" hides gradient tabs.
 *   children      – popover trigger element
 *   swatches      – optional array of hex swatches
 *   className     – extra class on PopoverContent
 */
function ColorPicker({
  value,
  children,
  swatches = [],
  onValueChange,
  className,
  projectId,
  mode = 'any',
  ...props
}) {
  // Determine initial tab from incoming value
  function detectTab(v) {
    if (!v) return 'solid';
    if (v.startsWith('linear-gradient')) return 'linear';
    if (v.startsWith('radial-gradient')) return 'radial';
    return 'solid';
  }

  const [tab, setTab] = React.useState(() => detectTab(value));
  const [colorHsv, setColorHsv] = React.useState(() => {
    if (!value || value.startsWith('linear') || value.startsWith('radial')) {
      return { h: 210, s: 0.3, v: 0.87, a: 1 };
    }
    try { return hexToHsva(value); } catch { return { h: 0, s: 0, v: 0, a: 1 }; }
  });

  const [stops, setStops] = React.useState(() => {
    const parsed = value
      ? (parseLinear(value) || parseRadial(value))
      : null;
    return parsed
      ? parsed.stops
      : [{ color: '#B8CCDF', position: 0 }, { color: '#D4B896', position: 100 }];
  });
  const [activeStop, setActiveStop] = React.useState(0);
  const [angle, setAngle] = React.useState(() => {
    if (!value) return 135;
    const p = parseLinear(value);
    return p ? p.angle : 135;
  });
  const [radialPos, setRadialPos] = React.useState(() => {
    if (!value) return { x: 50, y: 50 };
    const p = parseRadial(value);
    return p ? { x: p.posX, y: p.posY } : { x: 50, y: 50 };
  });

  const [recentColors, setRecentColors] = React.useState(() => getRecent(projectId));
  const [hexInput, setHexInput] = React.useState('');
  const [opacityInput, setOpacityInput] = React.useState('100');
  const internalChange = React.useRef(false);
  const valueChangedInSession = React.useRef(false);

  // Sync hex/opacity inputs from colorHsv
  React.useEffect(() => {
    setHexInput(hsvaToHex(colorHsv));
    setOpacityInput(Math.round(colorHsv.a * 100).toString());
  }, [colorHsv]);

  // Sync external value prop
  React.useEffect(() => {
    if (internalChange.current) { internalChange.current = false; return; }
    if (!value) return;
    const newTab = detectTab(value);
    setTab(newTab);
    if (newTab === 'solid') {
      try { setColorHsv(hexToHsva(value)); } catch {}
    } else if (newTab === 'linear') {
      const p = parseLinear(value);
      if (p) { setStops(p.stops); setAngle(p.angle); }
    } else if (newTab === 'radial') {
      const p = parseRadial(value);
      if (p) { setStops(p.stops); setRadialPos({ x: p.posX, y: p.posY }); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // When active stop changes, sync colorHsv to that stop's color
  React.useEffect(() => {
    if (tab !== 'solid') {
      try { setColorHsv(hexToHsva(stops[activeStop].color)); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStop, tab]);

  function emitSolid(hsva) {
    const hex = hsva.a < 1 ? hsvaToHexa(hsva) : hsvaToHex(hsva);
    onValueChange?.(hex);
  }

  function emitGradient(newStops, newAngle, newPos) {
    const s = newStops || stops;
    if (tab === 'linear') {
      onValueChange?.(buildLinear(newAngle ?? angle, s));
    } else {
      const p = newPos || radialPos;
      onValueChange?.(buildRadial(p.x, p.y, s));
    }
  }

  function handleHsvChange(newHsv) {
    internalChange.current = true;
    valueChangedInSession.current = true;
    setColorHsv(newHsv);
    if (tab === 'solid') {
      emitSolid(newHsv);
    } else {
      // update the active stop
      const newStops = stops.map((s, i) =>
        i === activeStop ? { ...s, color: hsvaToHex(newHsv) } : s
      );
      setStops(newStops);
      emitGradient(newStops);
    }
  }

  function handleAlphaChange(newHsv) {
    // Alpha component returns full hsva
    internalChange.current = true;
    valueChangedInSession.current = true;
    const updated = { ...colorHsv, a: newHsv.a };
    setColorHsv(updated);
    if (tab === 'solid') emitSolid(updated);
  }

  function handleHexInput(e) {
    const v = e.target.value;
    setHexInput(v);
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      try {
        const hsva = { ...hexToHsva(v), a: colorHsv.a };
        internalChange.current = true;
        valueChangedInSession.current = true;
        setColorHsv(hsva);
        if (tab === 'solid') {
          emitSolid(hsva);
        } else {
          const newStops = stops.map((s, i) =>
            i === activeStop ? { ...s, color: v } : s
          );
          setStops(newStops);
          emitGradient(newStops);
        }
      } catch {}
    }
  }

  function handleOpacityInput(e) {
    const raw = e.target.value.replace('%', '');
    setOpacityInput(raw);
    const num = parseInt(raw, 10);
    if (!isNaN(num) && num >= 0 && num <= 100) {
      const a = num / 100;
      const updated = { ...colorHsv, a };
      internalChange.current = true;
      valueChangedInSession.current = true;
      setColorHsv(updated);
      if (tab === 'solid') emitSolid(updated);
    }
  }

  function handleAngleChange(e) {
    const a = parseInt(e.target.value, 10);
    setAngle(a);
    internalChange.current = true;
    valueChangedInSession.current = true;
    emitGradient(undefined, a);
  }

  function handleRadialPos(x, y) {
    const pos = { x, y };
    setRadialPos(pos);
    internalChange.current = true;
    valueChangedInSession.current = true;
    emitGradient(undefined, undefined, pos);
  }

  function handleTabChange(t) {
    setTab(t);
    // Emit current value in new mode
    if (t === 'solid') {
      emitSolid(colorHsv);
    } else if (t === 'linear') {
      onValueChange?.(buildLinear(angle, stops));
    } else {
      onValueChange?.(buildRadial(radialPos.x, radialPos.y, stops));
    }
  }

  function handleOpenChange(open) {
    if (!open && valueChangedInSession.current) {
      const finalValue = tab === 'solid'
        ? (colorHsv.a < 1 ? hsvaToHexa(colorHsv) : hsvaToHex(colorHsv))
        : tab === 'linear' ? buildLinear(angle, stops) : buildRadial(radialPos.x, radialPos.y, stops);
      pushRecent(projectId, finalValue);
      setRecentColors(getRecent(projectId));
      valueChangedInSession.current = false;
    }
  }

  // Swatch preview for the trigger area
  const previewStyle = tab === 'solid'
    ? { background: hsvaToHex(colorHsv) }
    : tab === 'linear'
      ? { background: buildLinear(angle, stops) }
      : { background: buildRadial(radialPos.x, radialPos.y, stops) };

  const showTabs = mode === 'any';
  // Show alpha only for solid (gradient stops don't support alpha in v1)
  const showAlpha = tab === 'solid';

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className={cn('p-0', className)}
        style={{
          width: 296,
          '--selected-color': hsvaToHslString(colorHsv),
          background: 'var(--orbis-s1)',
          border: '1px solid var(--orbis-s2)',
        }}
        {...props}
      >
        <div className="p-3 space-y-3">
          {/* Tab bar */}
          {showTabs && (
            <div
              className="flex rounded-lg overflow-hidden"
              style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s2)' }}
            >
              {['solid', 'linear', 'radial'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleTabChange(t)}
                  className="flex-1 py-1.5 text-[11px] font-medium transition-colors capitalize"
                  style={{
                    background: tab === t ? 'var(--orbis-s2)' : 'transparent',
                    color: tab === t ? 'var(--orbis-t1)' : 'var(--orbis-t3)',
                    fontFamily: 'var(--orbis-font)',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          )}

          {/* Gradient stop selector (linear + radial) */}
          {tab !== 'solid' && (
            <StopSelector
              stops={stops}
              activeStop={activeStop}
              setActiveStop={setActiveStop}
            />
          )}

          {/* Saturation square */}
          <Saturation
            hsva={colorHsv}
            onChange={(newColor) => handleHsvChange({ ...colorHsv, ...newColor })}
            style={{
              width: '100%',
              height: 'auto',
              aspectRatio: '4/2.2',
              borderRadius: '6px',
            }}
            className="border border-[var(--orbis-s2)]"
          />

          {/* Hue slider */}
          <Hue
            hue={colorHsv.h}
            onChange={(newHue) => handleHsvChange({ ...colorHsv, ...newHue })}
            className={SLIDER_CLASS}
            style={SLIDER_STYLE}
          />

          {/* Alpha slider (solid only) */}
          {showAlpha && (
            <Alpha
              hsva={colorHsv}
              onChange={handleAlphaChange}
              height={12}
              className={SLIDER_CLASS}
              style={SLIDER_STYLE}
            />
          )}

          {/* Hex + opacity row */}
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded shrink-0 border border-[var(--orbis-s2)]"
              style={previewStyle}
            />
            <Input
              className="flex-1 bg-[var(--orbis-bg)] border-[var(--orbis-s2)] text-[var(--orbis-t1)] text-xs font-mono h-7 px-2"
              value={hexInput}
              onChange={handleHexInput}
              onBlur={() => setHexInput(hsvaToHex(colorHsv))}
              spellCheck={false}
            />
            {showAlpha && (
              <Input
                className="w-14 bg-[var(--orbis-bg)] border-[var(--orbis-s2)] text-[var(--orbis-t1)] text-xs font-mono h-7 px-2 text-right"
                value={opacityInput + '%'}
                onChange={handleOpacityInput}
                onFocus={(e) => setOpacityInput(e.target.value.replace('%', ''))}
                onBlur={() => setOpacityInput(Math.round(colorHsv.a * 100).toString())}
                spellCheck={false}
              />
            )}
          </div>

          {/* Linear: angle slider */}
          {tab === 'linear' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-[var(--orbis-t4)]">Angle</span>
                <span className="text-[10px] text-[var(--orbis-t3)] font-mono">{angle}°</span>
              </div>
              <input
                type="range"
                min={0}
                max={360}
                value={angle}
                onChange={handleAngleChange}
                className="w-full h-1 rounded appearance-none cursor-pointer"
                style={{ accentColor: 'var(--orbis-blue)' }}
              />
            </div>
          )}

          {/* Radial: position grid */}
          {tab === 'radial' && (
            <RadialGrid posX={radialPos.x} posY={radialPos.y} onChange={handleRadialPos} />
          )}

          {/* Gradient preview bar */}
          {tab !== 'solid' && (
            <div
              className="w-full h-5 rounded"
              style={{
                background: tab === 'linear'
                  ? buildLinear(angle, stops)
                  : buildRadial(radialPos.x, radialPos.y, stops),
                border: '1px solid var(--orbis-s2)',
              }}
            />
          )}

          {/* Swatches */}
          {swatches.length > 0 && (
            <>
              <div className="h-px bg-[var(--orbis-s2)]" />
              <div className="flex flex-wrap gap-1.5">
                {swatches.map((color) => (
                  <button
                    type="button"
                    key={color}
                    onClick={() => {
                      try {
                        const hsva = hexToHsva(color);
                        handleHsvChange(hsva);
                      } catch {}
                    }}
                    className="w-5 h-5 rounded cursor-pointer ring-1 ring-[var(--orbis-s2)] hover:ring-[var(--orbis-t1)] transition-all"
                    style={{ background: color }}
                  />
                ))}
              </div>
            </>
          )}

          {/* Recent colors */}
          {recentColors.length > 0 && (
            <>
              <div className="h-px bg-[var(--orbis-s2)]" />
              <div>
                <span className="text-[10px] text-[var(--orbis-t4)] mb-1.5 block">Recent</span>
                <div className="flex flex-wrap gap-1.5">
                  {recentColors.map((color, i) => (
                    <button
                      key={i}
                      type="button"
                      title={color}
                      onClick={() => {
                        if (color.startsWith('linear') || color.startsWith('radial')) {
                          if (showTabs) {
                            const t = color.startsWith('linear') ? 'linear' : 'radial';
                            handleTabChange(t);
                            // parse + apply
                            const p = t === 'linear' ? parseLinear(color) : parseRadial(color);
                            if (p) {
                              setStops(p.stops);
                              if (t === 'linear') setAngle(p.angle);
                              else setRadialPos({ x: p.posX, y: p.posY });
                            }
                            onValueChange?.(color);
                          }
                        } else {
                          try {
                            const hsva = hexToHsva(color);
                            if (showTabs) handleTabChange('solid');
                            handleHsvChange(hsva);
                          } catch {}
                        }
                      }}
                      className="w-5 h-5 rounded cursor-pointer ring-1 ring-[var(--orbis-s2)] hover:ring-[var(--orbis-t1)] transition-all shrink-0"
                      style={{
                        background: color.startsWith('linear') || color.startsWith('radial')
                          ? color
                          : color,
                      }}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { ColorPicker };
